import {
    Component,
    OnInit,
    OnDestroy,
    ViewChild,
    ElementRef,
    Renderer2,
    Inject,
} from "@angular/core";
import { NgForm } from "@angular/forms";
import { DOCUMENT } from "@angular/common";
import { HttpClient } from "@angular/common/http";
import { SeoService } from "../../../services/seo.service";

interface ChatMessage {
    text: string;
    sender: "user" | "bot";
    time: Date;
}

@Component({
    selector: "app-home-one",
    templateUrl: "./home-one.component.html",
    styleUrls: ["./home-one.component.scss"],
})
export class HomeOneComponent implements OnInit, OnDestroy {
    // Newsletter popup properties
    showNewsletterPopup: boolean = false;
    isSubmitting: boolean = false;
    isSuccess: boolean = false;
    isError: boolean = false;
    successMessage: string = "";
    errorMessage: string = "";
    firstName: string = "";
    lastName: string = "";
    email: string = "";

    // Chat widget properties
    isChatOpen: boolean = false;
    isSending: boolean = false;
    newMessage: string = "";
    unreadMessages: number = 0;
    messages: ChatMessage[] = [];

    @ViewChild("chatMessages") private chatMessages!: ElementRef;
    private structuredDataScript?: HTMLScriptElement;

    constructor(
        private http: HttpClient,
        private seoService: SeoService,
        private renderer2: Renderer2,
        @Inject(DOCUMENT) private _document: Document
    ) {}

    ngOnInit(): void {
        // Set SEO tags for home page
        this.seoService.setSEOTags(
            "Insightful IT Services | Enterprise Cloud & Managed IT",
            "Leading provider of enterprise cloud services in India. We offer GPU, Windows, and Linux cloud solutions with a 99.99% uptime SLA and 24/7 expert support.",
            "Cloud Services India, Managed IT Services, GPU Cloud, Enterprise Cloud, Windows Cloud, Linux Cloud, Cloud Infrastructure India, IT Support"
        );

        // Add structured data for SEO
        this.addStructuredData();

        // Initialize chat with a welcome message
        this.messages.push({
            text: "Hello! Welcome to Insightful IT Services. How can I help you today?",
            sender: "bot",
            time: new Date(),
        });
    }

    ngOnDestroy(): void {
        if (this.structuredDataScript) {
            this.renderer2.removeChild(
                this._document.head,
                this.structuredDataScript
            );
        }
    }

    closePopup(): void {
        this.showNewsletterPopup = false;
    }

    onSubmit(form: NgForm): void {
        if (form.invalid) {
            return;
        }

        this.isSubmitting = true;
        this.isSuccess = false;
        this.isError = false;

        // Prepare subscription data
        const subscriptionData = {
            email: this.email,
            firstName: this.firstName,
            lastName: this.lastName,
        };

        // Send subscription request to proxy server
        this.http
            .post("http://localhost:3001/api/subscribe", subscriptionData)
            .subscribe({
                next: (response: any) => {
                    this.isSubmitting = false;
                    this.isSuccess = true;
                    this.successMessage =
                        response.message ||
                        "Thank you for subscribing! Please check your email to confirm.";

                    // Hide popup after 3 seconds on success
                    setTimeout(() => {
                        this.showNewsletterPopup = false;
                    }, 3000);
                },
                error: (error) => {
                    this.isSubmitting = false;
                    this.isError = true;

                    // Provide user-friendly error messages
                    if (error.status === 400) {
                        if (error.error?.error === "Member Exists") {
                            this.errorMessage =
                                "This email is already subscribed to our newsletter.";
                        } else if (error.error?.error === "Invalid Resource") {
                            this.errorMessage =
                                "Please provide a valid email address.";
                        } else {
                            this.errorMessage =
                                error.error?.message ||
                                "Please provide a valid email address.";
                        }
                    } else if (error.status === 401) {
                        this.errorMessage =
                            "Authentication error. Please contact the site administrator.";
                    } else if (error.status === 404) {
                        this.errorMessage =
                            "Subscription list not found. Please contact the site administrator.";
                    } else {
                        this.errorMessage =
                            error.error?.message ||
                            "An error occurred while submitting your request. Please try again later.";
                    }
                },
            });
    }

    // Chat widget methods
    toggleChat(): void {
        this.isChatOpen = !this.isChatOpen;
        if (this.isChatOpen) {
            this.unreadMessages = 0;
            // Scroll to bottom when chat opens
            setTimeout(() => {
                this.scrollToBottom();
            }, 100);
        }
    }

    sendMessage(): void {
        if (!this.newMessage.trim() || this.isSending) {
            return;
        }

        // Add user message
        const userMessage: ChatMessage = {
            text: this.newMessage,
            sender: "user",
            time: new Date(),
        };

        this.messages.push(userMessage);
        this.isSending = true;

        // Clear input
        const userText = this.newMessage;
        this.newMessage = "";

        // Scroll to bottom
        setTimeout(() => {
            this.scrollToBottom();
        }, 100);

        // Simulate bot response after a delay
        setTimeout(() => {
            this.generateBotResponse(userText);
            this.isSending = false;

            // Scroll to bottom after bot response
            setTimeout(() => {
                this.scrollToBottom();
            }, 100);

            // Increase unread count if chat is closed
            if (!this.isChatOpen) {
                this.unreadMessages++;
            }
        }, 1000);
    }

    generateBotResponse(userMessage: string): void {
        let botResponse = "";

        // Simple response logic based on keywords
        const lowerMessage = userMessage.toLowerCase();

        if (
            lowerMessage.includes("hello") ||
            lowerMessage.includes("hi") ||
            lowerMessage.includes("hey")
        ) {
            botResponse = "Hello there! How can I assist you today?";
        } else if (
            lowerMessage.includes("cloud") ||
            lowerMessage.includes("gpu") ||
            lowerMessage.includes("windows") ||
            lowerMessage.includes("linux")
        ) {
            botResponse =
                "We offer various cloud solutions including GPU Cloud, Windows Cloud, and Linux Cloud. You can learn more about our services on our website or contact our sales team for detailed information.";
        } else if (
            lowerMessage.includes("price") ||
            lowerMessage.includes("cost") ||
            lowerMessage.includes("pricing")
        ) {
            botResponse =
                "For detailed pricing information, please contact our sales team who can provide you with customized solutions based on your requirements.";
        } else if (
            lowerMessage.includes("support") ||
            lowerMessage.includes("help") ||
            lowerMessage.includes("contact")
        ) {
            botResponse =
                "Our support team is available 24/7. You can reach us via chat, email at info@insightfulitservices.com, or phone at +91 9167663638.";
        } else if (lowerMessage.includes("thank")) {
            botResponse =
                "You're welcome! Is there anything else I can help you with?";
        } else {
            // Default response
            const responses = [
                "Thanks for your message. Our team will get back to you shortly.",
                "I've received your message. One of our experts will contact you soon.",
                "Great question! I've forwarded your inquiry to our support team.",
                "I understand your concern. Our team will assist you as soon as possible.",
                "Thanks for reaching out. We'll respond to your query within 24 hours.",
            ];
            botResponse =
                responses[Math.floor(Math.random() * responses.length)];
        }

        // Add bot message
        const botMessage: ChatMessage = {
            text: botResponse,
            sender: "bot",
            time: new Date(),
        };

        this.messages.push(botMessage);
    }

    scrollToBottom(): void {
        try {
            this.chatMessages.nativeElement.scrollIntoView({
                behavior: "smooth",
                block: "end",
                inline: "nearest",
            });
        } catch (err) {
            console.log("Could not scroll to bottom:", err);
        }
    }

    addStructuredData(): void {
        const script = this.renderer2.createElement("script");
        script.type = "application/ld+json";
        script.text = `{
            "@context": "https://schema.org",
            "@graph": [
                {
                    "@type": "Organization",
                    "@id": "https://www.insightfulitservices.com/#organization",
                    "name": "Insightful IT Services",
                    "alternateName": "Insightful IT Services LLP",
                    "url": "https://www.insightfulitservices.com/",
                    "logo": "https://www.insightfulitservices.com/assets/img/logo.png",
                    "contactPoint": {
                        "@type": "ContactPoint",
                        "telephone": "+91-9167663638",
                        "contactType": "customer service",
                        "areaServed": "IN",
                        "availableLanguage": "en"
                    },
                    "sameAs": [
                        "https://www.facebook.com/InsightfulITS",
                        "https://x.com/insightfulits",
                        "https://www.instagram.com/insightfulits/",
                        "https://www.linkedin.com/company/insightful-it-services/",
                        "https://www.youtube.com/@InsightfulITS"
                    ]
                },
                {
                    "@type": "WebSite",
                    "@id": "https://www.insightfulitservices.com/#website",
                    "url": "https://www.insightfulitservices.com/",
                    "name": "Insightful IT Services",
                    "description": "Leading provider of enterprise cloud services in India. We offer GPU, Windows, and Linux cloud solutions with a 99.99% uptime SLA and 24/7 expert support.",
                    "publisher": {
                        "@id": "https://www.insightfulitservices.com/#organization"
                    }
                },
                {
                    "@type": "ProfessionalService",
                    "name": "Insightful IT Services",
                    "image": "https://www.insightfulitservices.com/assets/img/logo.png",
                    "url": "https://www.insightfulitservices.com/",
                    "telephone": "+91-9167663638",
                    "address": {
                        "@type": "PostalAddress",
                        "addressLocality": "Mumbai",
                        "addressRegion": "MH",
                        "addressCountry": "IN"
                    },
                    "areaServed": {
                        "@type": "Country",
                        "name": "India"
                    },
                    "provider": { "@id": "https://www.insightfulitservices.com/#organization" },
                    "serviceType": ["Cloud Services", "Managed IT Services", "GPU Cloud Computing", "Windows Cloud Hosting", "Linux Cloud Hosting"]
                }
            ]
        }`;
        this.renderer2.appendChild(this._document.head, script);
        this.structuredDataScript = script;
    }
}
